import { CSSProperties, FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
// 组件
import { ProInputNumber, ProInput, ProSelect, } from '../antd';
import SuperSelect from '../SuperSelect';
// 方法
import classNames from "classnames";
// 常量
import { inputNumberStyle, IntervalTypeOption, IntervalTypeProps, IntervalTypeValue } from "./interface";
import { MAX_TAG_TEXT_LENGTH, TABLE_COLUMN_EMPTY } from "../constants/constant";
import './style.less';

/** 类型+区间 */
const IntervalType: FC<IntervalTypeProps> = props => {
  const {
    value: outValue, options, superSelectProps, selectFlex, intervalFlex, intervalSplitText, tagsSplitText,
    disabled, disabledRender, disabledBorder, disabledEllipsis, defauleValue,
    onChange,
  } = props;
  const [ value, setValue ] = useState<IntervalTypeValue | undefined>(defauleValue);
  const isFirstLoading = useRef<boolean>(true); // 是否首次加载
  useEffect(() => {
    if (!isFirstLoading.current || outValue) {
      setValue(outValue);
    }
    isFirstLoading.current = false;
  }, [outValue]);
  /** 下拉选项 */
  const renderOptions: any[] = useMemo(() => {
    if (superSelectProps?.options) return superSelectProps?.options;
    if (Array.isArray(options) && options.length) return options;
    return [];
  }, []);
  /** 下拉框值改变 */
  const handleSelectChange = (value: any) => {
    let newValue: IntervalTypeValue | undefined = { type: value, };
    if (value === undefined || value === null) {
      newValue = undefined;
    }
    setValue(newValue);
    onChange?.(newValue);
  };
  /** 区间开始值改变 */
  const handleIntervalStartChange = (value: any) => {
    setValue(prev => {
      const { type, interval } = prev ?? {};
      const intervalEnd = Array.isArray(interval) ? interval[1] : undefined;
      let newValue: IntervalTypeValue = { type, interval: [value, intervalEnd]};
      if ((value === undefined || value === null) && (intervalEnd === undefined || intervalEnd === null)) {
        newValue = { type };
      }
      onChange?.(newValue);
      return newValue;
    });
  };
  /** 区间结束值改变 */
  const handleIntervalEndChange = (value: any) => {
    setValue(prev => {
      const { type, interval } = prev ?? {};
      const intervalStart = Array.isArray(interval) ? interval[0] : undefined;
      let newValue: IntervalTypeValue = { type, interval: [intervalStart, value]};
      if ((value === undefined || value === null) && (intervalStart === undefined || intervalStart === null)) {
        newValue = { type };
      }
      onChange?.(newValue);
      return newValue;
    });
  };
  /** 区间值改变 */
  const handleIntervalChange = (value: any) => {
    setValue(prev => {
      let newValue: IntervalTypeValue = { ...prev, interval: value};
      if (value === undefined || value === null) {
        newValue = { type: prev?.type }
      }
      onChange?.(newValue);
      return newValue;
    });
  };
  /** 区间表单 */
  const renderInterval = useMemo(() => {
    if (!value || !renderOptions) return null;
    const { type, interval } = value;
    const findOption: IntervalTypeOption = renderOptions.find(item => item.value === type);
    if (findOption) {
      const { formItemType, inputProps, inputNumberProps, inputNumbersProps, selectTagsProps, } = findOption;
      let renderNodes: ReactNode[] = [];
      switch (formItemType) {
        case 'Input':
          renderNodes.push(
            <div className="interval-value">
              <ProInput
                style={inputNumberStyle}
                {...inputProps}
                key='intervalInput'
                value={typeof interval === 'string' ? interval : undefined}
                onChange={event => {
                  handleIntervalChange(event.target.value);
                  inputProps?.onChange?.(event);
                }}
              />
            </div>
          );
          break;
        case 'InputNumber':
          renderNodes.push(
            <div className="interval-value">
              <ProInputNumber
                style={inputNumberStyle}
                {...inputNumberProps}
                key='intervalNumber'
                value={typeof interval === 'number' ? interval : undefined}
                onChange={val => {
                  handleIntervalChange(val);
                  inputNumberProps?.onChange?.(val);
                }}
              />
            </div>
          );
          break;
        case 'SelectTags':
          renderNodes.push(
            <div className="interval-value">
              <ProSelect
                style={inputNumberStyle}
                value={Array.isArray(interval) ? interval : undefined}
                placeholder={`请输入${findOption.label}，多个请按回车分开`}
                maxTagCount={1}
                maxTagTextLength={MAX_TAG_TEXT_LENGTH}
                {...selectTagsProps}
                key='intervalTags'
                mode="tags"
                onChange={(val, items) => {
                  handleIntervalChange(val);
                  selectTagsProps?.onChange?.(val, items);
                }}
              />
            </div>
          );
          break;
        case 'NumberInterval':
          const beforeInputNumberProps = inputNumbersProps?.[0];
          const afterInputNumberProps = inputNumbersProps?.[0];
          renderNodes.push(
            <div className="interval-start">
              <ProInputNumber
                style={inputNumberStyle}
                {...beforeInputNumberProps}
                key='intervalStart'
                value={Array.isArray(interval) ? interval[0] : undefined}
                onChange={val => {
                  handleIntervalStartChange(val);
                  beforeInputNumberProps?.onChange?.(val);
                }}
              />
            </div>
          );
          if (intervalSplitText) {
            renderNodes.push(
              <div className="interval-split-text">{intervalSplitText}</div>
            );
          }
          renderNodes.push(
            <div className="interval-end">
              <ProInputNumber
                style={inputNumberStyle}
                {...afterInputNumberProps}
                key='intervalEnd'
                value={Array.isArray(interval) ? interval[1] : undefined}
                onChange={val => {
                  handleIntervalEndChange(val);
                  afterInputNumberProps?.onChange?.(val);
                }}
              />
            </div>
          );
          break;
      }
      if (findOption.afterNode) {
        renderNodes.push(
          <div className="p-l-s">{findOption.afterNode}</div>
        );
      }
      return renderNodes;
    }
  }, [intervalSplitText, value, renderOptions]);
  /** 禁用文字 */
  const disabledRenderHtml = () => {
    if (disabledRender) return disabledRender;
    let disabledText: string = '';
    if (value) {
      const { type, interval } = value;
      if (renderOptions) {
        const findItem = (renderOptions as IntervalTypeOption[]).find?.((item: any) => item?.value === type);
        if (findItem) {
          disabledText += `${findItem.label} - `;
          if (Array.isArray(interval)) {
            const splitText = findItem.formItemType === 'NumberInterval' ? intervalSplitText : tagsSplitText;
            disabledText += interval.join(splitText || ' - ');
          } else {
            disabledText += interval;
          }
          if (findItem.afterNode) {
            disabledText += `（${findItem.afterNode}）`;
          }
        }
      }
    }
    return disabledText || TABLE_COLUMN_EMPTY;
  };
  /** 下拉框容器样式 */
  const selectStyle: CSSProperties = useMemo(() => {
    const newStyle: CSSProperties = {};
    if (selectFlex) newStyle.flex = selectFlex;
    return newStyle;
  }, [selectFlex]);
  /** 区间容器样式 */
  const intervalStyle: CSSProperties = useMemo(() => {
    const newStyle: CSSProperties = {};
    if (intervalFlex) newStyle.flex = intervalFlex;
    return newStyle;
  }, [intervalFlex]);
  /** 样式 */
  const classes = useMemo(() => {
    return classNames('yb-interval-type', {
      'yb-interval-type-active': !!value?.type,
    });
  }, [value?.type]);
  /** 禁用样式 */
  const disabledClasses = useMemo(() => {
    return classNames('yb-interval-type-disabled', {
      'border-bottom-dashed': disabledBorder,
      'yb-line-ellipsis-1': disabledEllipsis,
    });
  }, [disabledBorder, disabledEllipsis]);
  return (
    <>
      {
        disabled ? (
          <div className={disabledClasses}>
            {disabledRenderHtml()}
          </div>
        ) : (
          <div className={classes}>
            <div className="yb-interval-type-select" style={selectStyle}>
              <SuperSelect
                allowClear
                {...superSelectProps}
                value={value?.type}
                options={renderOptions}
                onChange={handleSelectChange}
              />
            </div>
            <div className="yb-interval-type-values" style={intervalStyle}>
              {renderInterval}
            </div>
          </div>
        )
      }
    </>
  );
};

IntervalType.defaultProps = {
  selectFlex: 1,
  intervalFlex: 1,
  intervalSplitText: '到',
  tagsSplitText: '，'
};

export default IntervalType;
